// Phase 16 PUSH-06 — typed net wrapper for the preferences PATCH endpoint.
//
// Hits `PATCH /api/push-token` — the Phase 16 additive surface landed by
// Plan 02 (openapi.yaml + PushPreferencesPatch schema) and Plan 05 (server-
// side handler in apps/backend/src/push/handlers/preferences.ts). Per-account
// (one push_tokens row per user — see 16-CONTEXT.md line 50); the bearer's
// `user_uuid` is the only KV key, client-supplied user_uuid in the body is
// structurally ignored by the server (T-16-05-01).
//
// IMPORTANT — Idempotency-Key:
// Mutating ops carry a fresh UUIDv4 key per call. apiFetch's 5xx single-retry
// preserves the SAME key on the retry (RFC 9457 semantics; Phase 3 FIFO-50
// idempotency cache). The server is also naturally idempotent at the resource
// level (whole-field replace per supplied field), so an idempotency cache
// miss still produces the same final state.
//
// Verbatim shape from `apps/web/src/net/profile.ts::changeDisplayName` per
// 16-RESEARCH.md §"Net Wrappers" — same idempotent-PATCH idiom.

import type { components } from '@runegambit/api-types'

import { apiFetch } from './client'
import { createIdempotencyKey } from './idempotency'

/**
 * Client-facing prefs shape. The server's PATCH /api/push-token accepts
 * `PushPreferencesPatch` (both fields optional, at least one required) — the
 * client wrapper instead always sends BOTH fields per UI-SPEC §"Toast & State
 * Feedback" (the Settings UI does NOT field-level patch; it sends the whole
 * prefs object each Save). The server's whole-field replace semantics (Plan
 * 05) are the matching contract.
 *
 * Mirrors the closed 4-key triggers shape from the generated
 * `PushPreferencesPatch.triggers` (Plan 02 OpenAPI extension) and the
 * `QuietHours` schema (Phase 7) — both validated server-side.
 */
export interface NotificationPrefs {
  readonly triggers: {
    readonly opponent_moved: boolean
    readonly bot_substitution: boolean
    readonly daily_streak: boolean
    readonly tournament_cycle: boolean
  }
  readonly quiet_hours: {
    readonly start_hour: number
    readonly end_hour: number
    readonly tz: string
  }
}

// Compile-time sanity check: NotificationPrefs.triggers must match the closed
// 4-key OpenAPI shape exactly. If a future OpenAPI extension adds a new
// trigger key, this assignment fails tsc and forces a coordinated update.
type _TriggersShapeCheck = NonNullable<
  components['schemas']['PushPreferencesPatch']['triggers']
>
const _triggers_check: keyof NotificationPrefs['triggers'] extends keyof _TriggersShapeCheck
  ? keyof _TriggersShapeCheck extends keyof NotificationPrefs['triggers']
    ? true
    : never
  : never = true
void _triggers_check

/** Server response shape: `{ saved: true }` on 200 OK (Plan 05 handler). */
interface SaveResponse {
  readonly saved: boolean
}

/**
 * PATCH /api/push-token — save user notification preferences.
 *
 * Used by Plan 07's Settings → Notifications card. The wrapper sends BOTH
 * `triggers` and `quiet_hours` every call — the Settings UI emits a whole-
 * prefs Save per UI-SPEC, not field-level partial-patch. The server (Plan 05)
 * does whole-field replace per supplied field, so both shapes resolve to the
 * same final state.
 *
 * Idempotency-Key (UUIDv4) attached per call. apiFetch's 5xx single-retry
 * reuses the same key (RFC 9457). The server's Phase 3 FIFO-50 idempotency
 * cache deduplicates server-side; resource is also naturally idempotent
 * (whole-field replace).
 *
 * Error handling:
 *   - 4xx / 401 → throws ApiError (caller surfaces inline `save_error` per
 *     UI-SPEC §"Toast & State Feedback" — optimistic flip rolls back).
 *   - 5xx → apiFetch retries once with the same key; on second 5xx, throws.
 *   - Network error → ApiError(0) after one retry (apiFetch contract).
 */
export async function saveNotificationPrefs(
  prefs: NotificationPrefs,
): Promise<void> {
  await apiFetch<SaveResponse>('/api/push-token', 'PATCH', {
    body: {
      triggers: prefs.triggers,
      quiet_hours: prefs.quiet_hours,
    },
    idempotencyKey: createIdempotencyKey(),
  })
}
